// purpose: ?perf=1 かつモバイル時だけ、固定長ring bufferへframe phaseと実体数を無割当記録し、実機の長frame内訳を表示する。

import { isMobilePerformanceDevice } from '../platform/mobilePerformance';

const QUERY_KEY = 'perf';
const ENABLED_TOKEN = '1';
const SAMPLE_CAPACITY = 240;
const OVERLAY_UPDATE_FRAMES = 30;
const LONG_FRAME_MS = 20;

export const PERF_PHASE_FAMILY_SYNC = 0;
export const PERF_PHASE_GAME_FEEL = 1;
export const PERF_PHASE_LAYER_STACK = 2;
export const PERF_PHASE_PIXI_RENDER = 3;

export const PERF_STAGE_IDLE = 0;
export const PERF_STAGE_PREPARING = 1;
export const PERF_STAGE_READY = 2;

export const PERF_RETIRE_IDLE = 0;
export const PERF_RETIRE_PENDING = 1;
export const PERF_RETIRE_BATCHING = 2;
export const PERF_RETIRE_UNLOADING = 3;

function isPerfQueryEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get(QUERY_KEY) === ENABLED_TOKEN;
}

/**
 * Diagnostic-only sampler. No instance is created unless both the device gate
 * and `?perf=1` pass, so ordinary play has no overlay, timer reads or buffers.
 *
 * Every per-frame field is a fixed typed array. The only string construction
 * happens when the explicitly requested overlay refreshes twice per second.
 */
export class FramePhaseSampler {
  private readonly simulationMs = new Float32Array(SAMPLE_CAPACITY);
  private readonly audioMs = new Float32Array(SAMPLE_CAPACITY);
  private readonly familySyncMs = new Float32Array(SAMPLE_CAPACITY);
  private readonly gameFeelMs = new Float32Array(SAMPLE_CAPACITY);
  private readonly layerStackMs = new Float32Array(SAMPLE_CAPACITY);
  private readonly pixiRenderMs = new Float32Array(SAMPLE_CAPACITY);
  private readonly uiPublishMs = new Float32Array(SAMPLE_CAPACITY);
  private readonly uiCommitMs = new Float32Array(SAMPLE_CAPACITY);
  private readonly totalMs = new Float32Array(SAMPLE_CAPACITY);

  private readonly catchUpSteps = new Uint8Array(SAMPLE_CAPACITY);
  private readonly uiPublished = new Uint8Array(SAMPLE_CAPACITY);
  private readonly projectileCount = new Uint16Array(SAMPLE_CAPACITY);
  private readonly particleCount = new Uint16Array(SAMPLE_CAPACITY);
  private readonly enemyCount = new Uint16Array(SAMPLE_CAPACITY);
  private readonly pickupCount = new Uint16Array(SAMPLE_CAPACITY);
  private readonly oneShotCount = new Uint16Array(SAMPLE_CAPACITY);
  private readonly stagePrepareState = new Uint8Array(SAMPLE_CAPACITY);
  private readonly stageRetireState = new Uint8Array(SAMPLE_CAPACITY);

  private cursor = 0;
  private size = 0;
  private currentSlot = -1;
  private frameStart = 0;
  private overlayCountdown = OVERLAY_UPDATE_FRAMES;
  private pendingUiSlot = -1;
  private pendingUiStart = 0;
  private overlay: HTMLPreElement | null = null;

  constructor() {
    this.overlay = this.createOverlay();
    this.updateOverlay();
  }

  beginFrame(now: number): void {
    // An external-store notification whose selected snapshots stayed equal
    // legitimately produces no React commit. Never attribute that publication
    // to an unrelated layout effect in a later frame.
    this.pendingUiSlot = -1;
    this.pendingUiStart = 0;
    const slot = this.cursor;
    this.currentSlot = slot;
    this.frameStart = now;
    this.simulationMs[slot] = 0;
    this.audioMs[slot] = 0;
    this.familySyncMs[slot] = 0;
    this.gameFeelMs[slot] = 0;
    this.layerStackMs[slot] = 0;
    this.pixiRenderMs[slot] = 0;
    this.uiPublishMs[slot] = 0;
    this.uiCommitMs[slot] = 0;
    this.totalMs[slot] = 0;
    this.catchUpSteps[slot] = 0;
    this.uiPublished[slot] = 0;
    this.projectileCount[slot] = 0;
    this.particleCount[slot] = 0;
    this.enemyCount[slot] = 0;
    this.pickupCount[slot] = 0;
    this.oneShotCount[slot] = 0;
    this.stagePrepareState[slot] = PERF_STAGE_IDLE;
    this.stageRetireState[slot] = PERF_RETIRE_IDLE;
  }

  recordSimulation(durationMs: number, steps: number): void {
    const slot = this.currentSlot;
    if (slot < 0) return;
    this.simulationMs[slot] = durationMs;
    this.catchUpSteps[slot] = Math.min(255, Math.max(0, steps | 0));
  }

  recordAudio(durationMs: number): void {
    const slot = this.currentSlot;
    if (slot >= 0) this.audioMs[slot] = durationMs;
  }

  recordRenderPhase(phase: number, durationMs: number): void {
    const slot = this.currentSlot;
    if (slot < 0) return;
    if (phase === PERF_PHASE_FAMILY_SYNC) this.familySyncMs[slot] = durationMs;
    else if (phase === PERF_PHASE_GAME_FEEL) this.gameFeelMs[slot] = durationMs;
    else if (phase === PERF_PHASE_LAYER_STACK) this.layerStackMs[slot] = durationMs;
    else if (phase === PERF_PHASE_PIXI_RENDER) this.pixiRenderMs[slot] = durationMs;
  }

  recordRenderCounts(
    projectiles: number,
    particles: number,
    enemies: number,
    pickups: number,
    oneShots: number,
  ): void {
    const slot = this.currentSlot;
    if (slot < 0) return;
    this.projectileCount[slot] = Math.max(0, projectiles | 0);
    this.particleCount[slot] = Math.max(0, particles | 0);
    this.enemyCount[slot] = Math.max(0, enemies | 0);
    this.pickupCount[slot] = Math.max(0, pickups | 0);
    this.oneShotCount[slot] = Math.max(0, oneShots | 0);
  }

  recordStageState(prepareState: number, retireState: number): void {
    const slot = this.currentSlot;
    if (slot < 0) return;
    this.stagePrepareState[slot] = prepareState;
    this.stageRetireState[slot] = retireState;
  }

  beginUiPublish(now: number): void {
    const slot = this.currentSlot;
    if (slot < 0) return;
    this.uiPublished[slot] = 1;
    this.pendingUiSlot = slot;
    this.pendingUiStart = now;
  }

  endUiPublish(durationMs: number): void {
    const slot = this.currentSlot;
    if (slot >= 0) this.uiPublishMs[slot] = durationMs;
  }

  /**
   * App calls this from a layout effect. It measures publish-start to the first
   * React commit caused by that external-store notification without requiring
   * React's profiling build.
   */
  noteReactCommit(now: number): void {
    const slot = this.pendingUiSlot;
    if (slot < 0) return;
    this.uiCommitMs[slot] = Math.max(0, now - this.pendingUiStart);
    this.pendingUiSlot = -1;
  }

  endFrame(now: number): void {
    const slot = this.currentSlot;
    if (slot < 0) return;
    this.totalMs[slot] = Math.max(0, now - this.frameStart);
    this.cursor = (slot + 1) % SAMPLE_CAPACITY;
    if (this.size < SAMPLE_CAPACITY) this.size += 1;
    this.currentSlot = -1;
    this.overlayCountdown -= 1;
    if (this.overlayCountdown <= 0) {
      this.overlayCountdown = OVERLAY_UPDATE_FRAMES;
      this.updateOverlay();
    }
  }

  dispose(): void {
    this.overlay?.remove();
    this.overlay = null;
    this.pendingUiSlot = -1;
    this.currentSlot = -1;
  }

  private createOverlay(): HTMLPreElement | null {
    if (typeof document === 'undefined') return null;
    const overlay = document.createElement('pre');
    overlay.dataset.luminaPerf = 'wave-2';
    overlay.setAttribute('aria-label', 'LUMINA ARCHIVE performance sampler');
    overlay.style.cssText = [
      'position:fixed',
      'z-index:2147483647',
      'top:8px',
      'left:8px',
      'max-width:calc(100vw - 16px)',
      'margin:0',
      'padding:8px 10px',
      'border:1px solid #71f7ff',
      'background:rgba(2,7,17,.9)',
      'color:#dffcff',
      'font:10px/1.35 ui-monospace,SFMono-Regular,Menlo,monospace',
      'white-space:pre-wrap',
      'pointer-events:none',
    ].join(';');
    document.body.appendChild(overlay);
    return overlay;
  }

  private updateOverlay(): void {
    const overlay = this.overlay;
    if (!overlay) return;
    const size = this.size;
    const latest = (this.cursor + SAMPLE_CAPACITY - 1) % SAMPLE_CAPACITY;
    let totalAverage = 0;
    let totalMax = 0;
    let simulationMax = 0;
    let audioMax = 0;
    let familyMax = 0;
    let feelMax = 0;
    let stackMax = 0;
    let pixiMax = 0;
    let uiPublishMax = 0;
    let uiCommitMax = 0;
    let longFrames = 0;
    let uiFrames = 0;
    for (let i = 0; i < size; i += 1) {
      const total = this.totalMs[i]!;
      totalAverage += total;
      if (total > totalMax) totalMax = total;
      if (total > LONG_FRAME_MS) longFrames += 1;
      if (this.simulationMs[i]! > simulationMax) simulationMax = this.simulationMs[i]!;
      if (this.audioMs[i]! > audioMax) audioMax = this.audioMs[i]!;
      if (this.familySyncMs[i]! > familyMax) familyMax = this.familySyncMs[i]!;
      if (this.gameFeelMs[i]! > feelMax) feelMax = this.gameFeelMs[i]!;
      if (this.layerStackMs[i]! > stackMax) stackMax = this.layerStackMs[i]!;
      if (this.pixiRenderMs[i]! > pixiMax) pixiMax = this.pixiRenderMs[i]!;
      if (this.uiPublishMs[i]! > uiPublishMax) uiPublishMax = this.uiPublishMs[i]!;
      if (this.uiCommitMs[i]! > uiCommitMax) uiCommitMax = this.uiCommitMs[i]!;
      uiFrames += this.uiPublished[i]!;
    }
    const average = size > 0 ? totalAverage / size : 0;
    overlay.textContent =
      `LUMINA PERF W2 · ${size}/${SAMPLE_CAPACITY} frames\n`
      + `frame avg ${average.toFixed(2)}  max ${totalMax.toFixed(2)}  >${LONG_FRAME_MS}ms ${longFrames}\n`
      + `max   sim ${simulationMax.toFixed(2)}  audio ${audioMax.toFixed(2)}  family ${familyMax.toFixed(2)}\n`
      + `      feel ${feelMax.toFixed(2)}  stack ${stackMax.toFixed(2)}  pixi ${pixiMax.toFixed(2)}\n`
      + `UI    publish ${uiPublishMax.toFixed(2)}  commit ${uiCommitMax.toFixed(2)}  frames ${uiFrames}\n`
      + `live  step ${this.catchUpSteps[latest]}  enemy ${this.enemyCount[latest]}  shot ${this.projectileCount[latest]}\n`
      + `      particle ${this.particleCount[latest]}  pickup ${this.pickupCount[latest]}  one-shot ${this.oneShotCount[latest]}\n`
      + `asset prepare ${stagePrepareLabel(this.stagePrepareState[latest]!)}  retire ${stageRetireLabel(this.stageRetireState[latest]!)}`;
  }
}

export function createMobileFramePhaseSampler(): FramePhaseSampler | null {
  return isPerfQueryEnabled() && isMobilePerformanceDevice()
    ? new FramePhaseSampler()
    : null;
}

function stagePrepareLabel(state: number): string {
  if (state === PERF_STAGE_PREPARING) return 'PREPARING';
  if (state === PERF_STAGE_READY) return 'READY';
  return 'IDLE';
}

function stageRetireLabel(state: number): string {
  if (state === PERF_RETIRE_PENDING) return 'PENDING';
  if (state === PERF_RETIRE_BATCHING) return 'BATCHING';
  if (state === PERF_RETIRE_UNLOADING) return 'UNLOADING';
  return 'IDLE';
}
